// Pure helpers for firmware upgrade request building and error formatting.
// Kept free of imports so the node --test suite can require them directly.

const INCOMPATIBLE_MODEL_FALLBACK = 'This firmware was not built for the hardware model of every unit you selected.';

/**
 * Build the upgrade_type field for a firmware upgrade POST body.
 *
 * When the operator selects "Auto" the sentinel value is an empty string.
 * Omitting the key from the body signals the backend to auto-select the
 * correct upgrader per device family (2-Series/4-Series/Vantage get
 * PERSISTENT; most others get NON302M). Sending a falsy value (including
 * null or undefined) is treated the same as Auto — the key is omitted.
 *
 * @param {string|null|undefined} upgradeType - Selected upgrade type or '' for Auto.
 * @returns {{ upgrade_type: string } | {}}
 */
export const buildUpgradeTypeField = upgradeType => (upgradeType ? { upgrade_type: upgradeType } : {});

/**
 * Format a mixed_upgrade_batch 400 error body into a human-readable string.
 *
 * The backend groups units by the upgrader they require (the ``breakdown``
 * map: { upgradeTypeName: [unitId, ...] }) when a single batch spans more
 * than one device family. We summarise counts so the operator knows how to
 * split the batch.
 *
 * @param {{ code: string, detail?: string, breakdown?: Record<string, number[]> }} data
 * @returns {string}
 */
export const formatMixedBatchError = data => {
  const base =
    'The selected units require different upgrade types and cannot be run as a single batch. ' +
    'Either choose an explicit upgrade type, or split the units into separate batches.';
  const breakdown = data?.breakdown;
  if (!breakdown || Object.keys(breakdown).length === 0) return base;
  const summary = Object.entries(breakdown)
    .map(([type, ids]) => `${type}: ${Array.isArray(ids) ? ids.length : ids} unit(s)`)
    .join(', ');
  return `${base}\n\nRequired upgraders: ${summary}.`;
};

/**
 * Build an id → model-name lookup from the SmartLink model reference catalog.
 *
 * Falls back to "#<id>" so a refusal still names the model when the catalog
 * hasn't loaded or the backend cites a model the FE has never seen.
 *
 * @param {Array<{id: number|string, display_name?: string}>} unitModels
 * @returns {(modelId: number|string) => string}
 */
export const buildModelNameResolver = unitModels => {
  const byId = new Map(
    (Array.isArray(unitModels) ? unitModels : []).map(model => [String(model.id), model.display_name]),
  );
  return modelId => byId.get(String(modelId)) || `#${modelId}`;
};

/**
 * Flatten an ``incompatible_firmware_model`` 400 body into display rows.
 *
 * The backend refuses the whole request and names every unit whose hardware
 * model the chosen image was not built for.
 *
 * @param {{ units?: Array<object> }} data - The 400 response body.
 * @param {(modelId: number|string) => string} [resolveModelName]
 * @returns {Array<{unitId: *, siteId: *, unitModel: string, firmwareModels: string}>}
 */
export const incompatibleFirmwareModelRows = (data, resolveModelName = modelId => `#${modelId}`) => {
  const units = Array.isArray(data?.units) ? data.units : [];
  return units.map(unit => ({
    unitId: unit.unit_id,
    siteId: unit.site_id ?? null,
    unitModel: unit.unit_model || resolveModelName(unit.unit_model_id),
    firmwareModels: (Array.isArray(unit.firmware_model_ids) ? unit.firmware_model_ids : [])
      .map(resolveModelName)
      .join(', '),
  }));
};

/**
 * Format an ``incompatible_firmware_model`` 400 body into a readable block.
 *
 * The server's own sentence leads; one line per offending unit follows so the
 * operator can see which units to pull out of the batch.
 *
 * @param {{ detail?: string, units?: Array<object> }} data
 * @param {(modelId: number|string) => string} [resolveModelName]
 * @returns {string}
 */
export const formatIncompatibleFirmwareModelError = (data, resolveModelName) => {
  const base = data?.detail || INCOMPATIBLE_MODEL_FALLBACK;
  const rows = incompatibleFirmwareModelRows(data, resolveModelName);
  if (rows.length === 0) return base;
  const lines = rows.map(row => {
    const where = row.siteId == null ? '' : ` (site ${row.siteId})`;
    const firmwareFor = row.firmwareModels || 'other models';
    return `• Unit ${row.unitId}${where} is a ${row.unitModel}; this firmware is for ${firmwareFor}.`;
  });
  return `${base}\n\n${lines.join('\n')}`;
};
